import { css } from '@firebolt-dev/css'
import { useEffect, useState } from 'react'

export function UIEditorFieldVec3({
  label,
  dp = 0,
  min = -Infinity,
  max = Infinity,
  step = 1,
  bigStep = 5,
  smallStep = 1,
  value,
  onChange,
}) {
  const formatNum = (num, dp) => {
    if (isNaN(num)) return '0'
    const rounded = num.toFixed(dp)
    // remove trailing zeros and a trailing decimal if no decimals remain
    return rounded.replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1')
  }
  let valueX = value?.[0] || 0
  let valueY = value?.[1] || 0
  let valueZ = value?.[2] || 0
  const [localX, setLocalX] = useState(formatNum(valueX, dp))
  const [localY, setLocalY] = useState(formatNum(valueY, dp))
  const [localZ, setLocalZ] = useState(formatNum(valueZ, dp))
  const [focused, setFocused] = useState(false)
  useEffect(() => {
    if (!focused) {
      if (localX !== formatNum(valueX, dp)) setLocalX(formatNum(valueX, dp))
      if (localY !== formatNum(valueY, dp)) setLocalY(formatNum(valueY, dp))
      if (localZ !== formatNum(valueZ, dp)) setLocalZ(formatNum(valueZ, dp))
    }
  }, [focused, valueX, valueY, valueZ])
  const parseStr = (str, ogValue) => {
    // try parse math
    let num
    try {
      num = (0, eval)(str)
      if (typeof num !== 'number') {
        throw new Error('input number parse fail')
      }
    } catch (err) {
      // console.error(err)
      num = ogValue // revert back to original
    }
    if (num < min || num > max) {
      num = ogValue
    }
    return num
  }
  return (
    <div
      className='fieldvec3'
      css={css`
        flex: 1;
        display: flex;
        align-items: center;
        gap: 8px;
        input {
          background: var(--bg4);
          border-radius: 5px;
          padding: 0 12px;
          height: 34px;
          text-align: right;
          overflow: hidden;
          cursor: inherit;
          &::selection {
            background-color: white;
            color: rgba(0, 0, 0, 0.8);
          }
        }
      `}
    >
      <input
        type='text'
        value={localX}
        onChange={e => setLocalX(e.target.value)}
        onKeyDown={e => {
          if (e.code === 'Enter') {
            e.preventDefault()
            e.target.blur()
          }
          if (e.code === 'ArrowUp') {
            const amount = e.shiftKey ? bigStep : e.altKey ? smallStep : step
            const num = parseStr(valueX + amount, valueX)
            setLocalX(formatNum(num, dp))
            onChange([+num.toFixed(dp), valueY, valueZ])
          }
          if (e.code === 'ArrowDown') {
            const amount = e.shiftKey ? bigStep : e.altKey ? smallStep : step
            const num = parseStr(valueX - amount, valueX)
            setLocalX(formatNum(num, dp))
            onChange([+num.toFixed(dp), valueY, valueZ])
          }
        }}
        onFocus={e => {
          setFocused(true)
          e.target.select()
        }}
        onBlur={e => {
          setFocused(false)
          // if blank, set back to original
          if (localX === '') {
            setLocalX(formatNum(valueX, dp))
            return
          }
          // otherwise run through pipeline
          const num = parseStr(localX)
          setLocalX(formatNum(num, dp))
          onChange([+num.toFixed(dp), valueY, valueZ])
        }}
      />
      <input
        type='text'
        value={localY}
        onChange={e => setLocalY(e.target.value)}
        onKeyDown={e => {
          if (e.code === 'Enter') {
            e.preventDefault()
            e.target.blur()
          }
          if (e.code === 'ArrowUp') {
            const amount = e.shiftKey ? bigStep : e.altKey ? smallStep : step
            const num = parseStr(valueY + amount, valueY)
            setLocalY(formatNum(num, dp))
            onChange([valueX, +num.toFixed(dp), valueZ])
          }
          if (e.code === 'ArrowDown') {
            const amount = e.shiftKey ? bigStep : e.altKey ? smallStep : step
            const num = parseStr(valueY - amount, valueY)
            setLocalY(formatNum(num, dp))
            onChange([valueX, +num.toFixed(dp), valueZ])
          }
        }}
        onFocus={e => {
          setFocused(true)
          e.target.select()
        }}
        onBlur={e => {
          setFocused(false)
          // if blank, set back to original
          if (localY === '') {
            setLocalY(formatNum(valueY, dp))
            return
          }
          // otherwise run through pipeline
          const num = parseStr(localY)
          setLocalY(formatNum(num, dp))
          onChange([valueX, +num.toFixed(dp), valueZ])
        }}
      />
      <input
        type='text'
        value={localZ}
        onChange={e => setLocalZ(e.target.value)}
        onKeyDown={e => {
          if (e.code === 'Enter') {
            e.preventDefault()
            e.target.blur()
          }
          if (e.code === 'ArrowUp') {
            const amount = e.shiftKey ? bigStep : e.altKey ? smallStep : step
            const num = parseStr(valueZ + amount, valueZ)
            setLocalZ(formatNum(num, dp))
            onChange([valueX, valueY, +num.toFixed(dp)])
          }
          if (e.code === 'ArrowDown') {
            const amount = e.shiftKey ? bigStep : e.altKey ? smallStep : step
            const num = parseStr(valueZ - amount, valueZ)
            setLocalZ(formatNum(num, dp))
            onChange([valueX, valueY, +num.toFixed(dp)])
          }
        }}
        onFocus={e => {
          setFocused(true)
          e.target.select()
        }}
        onBlur={e => {
          setFocused(false)
          // if blank, set back to original
          if (localZ === '') {
            setLocalZ(formatNum(valueZ, dp))
            return
          }
          // otherwise run through pipeline
          const num = parseStr(localZ)
          setLocalZ(formatNum(num, dp))
          onChange([valueX, valueY, +num.toFixed(dp)])
        }}
      />
    </div>
  )
}
